import { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  Lightning,
  Warning,
  ArrowClockwise,
  MapPin,
  CheckCircle,
  CloudSlash,
} from "@phosphor-icons/react";
import { toast } from "sonner";

function formatRelative(iso) {
  if (!iso) return "—";
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "ahora";
    if (mins < 60) return `hace ${mins}m`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `hace ${hours}h`;
    return `hace ${Math.floor(hours / 24)}d`;
  } catch {
    return iso;
  }
}

export default function OutagesWidget() {
  const [status, setStatus] = useState(null);
  const [affected, setAffected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const load = async () => {
    try {
      const [s, a] = await Promise.all([
        api.get("/outages/status"),
        api.get("/outages/affected"),
      ]);
      setStatus(s.data);
      setAffected(a.data || []);
    } catch (e) {
      if (process.env.NODE_ENV === "development") console.debug("[outages] load failed", e?.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 60_000); // refresh once a minute
    return () => clearInterval(iv);
  }, []);

  const forceSync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/outages/sync");
      toast.success(
        `Sync completado · ${data.clients_affected}/${data.clients_checked} clientes afectados`
      );
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync falló");
    } finally {
      setSyncing(false);
    }
  };

  const geocode = async () => {
    try {
      await api.post("/outages/geocode-batch?limit=50");
      toast.success("Geocoding en curso · vuelve a chequear en 1 minuto");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Geocoding falló");
    }
  };

  if (loading) return null;

  const last = status?.last_sync || {};
  const errors = last.errors_by_tdu || {};
  const feedHealthy = last.healthy !== false;
  const coverage = status?.coverage || {};

  return (
    <div
      className="bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-md overflow-hidden"
      data-testid="outages-widget"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100 dark:border-slate-900 bg-slate-50/40 dark:bg-slate-900/30">
        <div className="flex items-center gap-2">
          <Lightning
            size={13}
            weight="fill"
            className={affected.length > 0 ? "text-orange-500" : "text-slate-400"}
          />
          <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400 font-semibold">
            Interrupciones activas en tu zona
          </div>
          {last.at && (
            <>
              <div className="h-3 w-px bg-slate-300 dark:bg-slate-700" />
              <span className="text-[10px] text-slate-400 font-mono">
                {formatRelative(last.at)}
              </span>
            </>
          )}
        </div>
        <div className="flex items-center gap-1">
          {coverage.pct < 80 && (
            <button
              onClick={geocode}
              data-testid="outages-geocode"
              className="inline-flex items-center gap-1 px-2 h-7 text-[10px] font-semibold text-slate-500 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 rounded transition-colors"
              title={`Solo ${coverage.pct}% de clientes están geocodificados`}
            >
              <MapPin size={11} /> Geocodificar
            </button>
          )}
          <button
            onClick={forceSync}
            disabled={syncing}
            data-testid="outages-sync"
            className="inline-flex items-center gap-1 px-2 h-7 text-[10px] font-semibold text-slate-500 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 rounded transition-colors"
          >
            <ArrowClockwise size={11} className={syncing ? "animate-spin" : ""} /> Sync
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-slate-100 dark:divide-slate-900">
        {/* KPI: affected */}
        <div className="px-5 py-4">
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400 font-semibold">
            Clientes afectados
          </div>
          <div
            className={`mt-1 text-3xl font-bold font-mono tabular-nums ${
              affected.length > 0
                ? "text-orange-600 dark:text-orange-400"
                : "text-slate-900 dark:text-slate-100"
            }`}
          >
            {affected.length}
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            de {coverage.geocoded || 0} geocodificados
          </div>
        </div>

        {/* TDU health */}
        <div className="px-5 py-4">
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400 font-semibold">
            Feeds TDU
          </div>
          <div className="mt-1.5 space-y-1">
            {Object.entries(last.counts_by_tdu || {}).map(([tdu, count]) => {
              const err = errors[tdu];
              return (
                <div key={tdu} className="flex items-center justify-between text-[12px]">
                  <div className="flex items-center gap-1.5">
                    {err ? (
                      <CloudSlash size={11} className="text-rose-500" />
                    ) : (
                      <CheckCircle size={11} weight="fill" className="text-emerald-500" />
                    )}
                    <span className="text-slate-700 dark:text-slate-300">{tdu}</span>
                  </div>
                  <span className="font-mono tabular-nums text-slate-500">
                    {err ? "—" : `${count} zonas`}
                  </span>
                </div>
              );
            })}
            {!Object.keys(last.counts_by_tdu || {}).length && (
              <div className="text-[11px] text-slate-400 italic">Aún sin sincronizar</div>
            )}
          </div>
        </div>

        {/* Coverage */}
        <div className="px-5 py-4">
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400 font-semibold">
            Cobertura de detección
          </div>
          <div className="mt-1 text-2xl font-bold font-mono tabular-nums text-slate-900 dark:text-slate-100">
            {coverage.pct || 0}%
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            {coverage.geocoded || 0} / {coverage.total_active || 0} geocodificados
          </div>
        </div>
      </div>

      {/* Affected clients list */}
      {affected.length > 0 && (
        <div className="border-t border-slate-100 dark:border-slate-900">
          <div className="px-5 py-2 text-[10px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400 font-semibold bg-orange-50 dark:bg-orange-950/20 border-b border-orange-200 dark:border-orange-900/40">
            <Warning size={11} className="inline mr-1" /> Sin luz ahora
          </div>
          <div className="max-h-64 overflow-y-auto">
            {affected.slice(0, 20).map((c) => (
              <div
                key={c.id}
                className="px-5 py-2 border-b border-slate-100 dark:border-slate-900 last:border-0 hover:bg-slate-50 dark:hover:bg-slate-900/40 flex items-center justify-between gap-3"
                data-testid={`outage-affected-${c.id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">
                    {c.name}
                  </div>
                  <div className="text-[11px] text-slate-500 truncate">
                    {c.address} · {c.city} {c.zip_code}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-[10px] uppercase tracking-wider text-orange-600 dark:text-orange-400 font-semibold">
                    {c.outage_note?.split(" · ")[1] || "TDU"}
                  </div>
                  <div className="text-[10px] text-slate-400 font-mono">
                    {formatRelative(c.outage_detected_at)}
                  </div>
                </div>
              </div>
            ))}
            {affected.length > 20 && (
              <div className="px-5 py-2 text-[11px] text-slate-500 italic">
                +{affected.length - 20} más
              </div>
            )}
          </div>
        </div>
      )}

      {/* Feed error warning */}
      {!feedHealthy && (
        <div className="px-5 py-2 border-t border-amber-200 dark:border-amber-900/40 bg-amber-50 dark:bg-amber-950/20 text-[11px] text-amber-800 dark:text-amber-300 flex items-start gap-2">
          <Warning size={12} className="mt-0.5 shrink-0" />
          <span>
            Uno o más feeds TDU fallaron. Detección automática degradada — puedes verificar
            manualmente cada cliente desde su Perfil 360°.
          </span>
        </div>
      )}
    </div>
  );
}
